import { useLocation, useNavigate } from 'react-router-dom';
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import AdminProvider from '../providers/AdminProvider'; // Import the new AdminProvider
import { useTranslation } from 'react-i18next';
import './AuthContainer.css'; // Assuming you have an AuthContainer.css for styling

const AuthContainer = () => {
    const { t, i18n } = useTranslation();
    const { state } = useLocation();
    const navigate = useNavigate();

    // Retrieve admin token directly from localStorage
    const [adminToken, setAdminToken] = useState(localStorage.getItem('admin_token'));
    // Note: setAdminToken is not explicitly called here because adminToken is
    // expected to be set once by AuthLogin and then read from localStorage on mount.

    // Data for display and coupon creation
    const [games, setGames] = useState([]);
    const [users, setUsers] = useState([]);
    const [surveys, setSurveys] = useState([]); // New state for surveys
    const [surveysOffset, setSurveysOffset] = useState(0);
    const [hasMoreSurveys, setHasMoreSurveys] = useState(true);

    // Play History states
    const [playsHistory, setPlaysHistory] = useState([]);
    const [playsOffset, setPlaysOffset] = useState(0);
    const [hasMorePlays, setHasMorePlays] = useState(true);

    // Coupon Creation Form State
    const [selectedGame, setSelectedGame] = useState('');
    const [selectedUser, setSelectedUser] = useState(''); // Can be null if coupon is not assigned to a specific user
    const [couponMessage, setCouponMessage] = useState({ type: '', text: '' }); // 'success' | 'error'

    const [isLoading, setIsLoading] = useState(false);

    // Existing states for generic table management (keeping for now, but not directly used for coupon creation)
    const [selectedTable, setSelectedTable] = useState(null);
    const [tableData, setTableData] = useState([]);
    const [updateData, setUpdateData] = useState('');

    // Fetch games and users on component mount
    useEffect(() => {
        const adminProvider = new AdminProvider();
        const fetchInitialData = async () => {
            setIsLoading(true);
            try {
                // Fetch all games
                const fetchedGames = await adminProvider.getAllGames(i18n.language);
                setGames(fetchedGames);
                if (fetchedGames.length > 0) {
                    setSelectedGame(fetchedGames[0].id); // Select the first game by default
                }

                // Fetch all users
                const fetchedUsers = await adminProvider.getAllUsers();
                setUsers(fetchedUsers);

                 // Fetch first 10 surveys
                 const fetchedSurveys = await adminProvider.getAllSurveys(i18n.language, 10, 0);
                 setSurveys(fetchedSurveys);
                 setSurveysOffset(10);
                 if (fetchedSurveys.length < 10) setHasMoreSurveys(false);
               
                 // Fetch first 10 plays
                 const fetchedPlays = await adminProvider.getAllPlays(10, 0);
                 setPlaysHistory(fetchedPlays);
                 setPlaysOffset(10);
                 if (fetchedPlays.length < 10) setHasMorePlays(false);
               
                // We don't pre-select a user, as it's optional for coupons
            } catch (error) {
                console.error('Error fetching initial admin data:', error);
                setCouponMessage({ type: 'error', text: t('admin_error_fetching_data', { defaultValue: 'Error fetching initial data for admin panel.' }) });
            } finally {
                setIsLoading(false);
            }
        };

        if (adminToken) { // Only fetch if an admin token exists
            fetchInitialData();
        }
    }, [adminToken, i18n.language, t]);

    const handleCreateCoupon = async () => {
        setCouponMessage({ type: '', text: '' }); // Clear previous messages
        setIsLoading(true);

        // Basic client-side validation
        if (!selectedGame || !selectedUser) {
            setCouponMessage({ type: 'error', text: t('admin_coupon_missing_fields', { defaultValue: 'Please fill in all required coupon fields.' }) });
            setIsLoading(false);
            return;
        }

        const couponPayload = {
            gameId: selectedGame,
            userId: selectedUser 
        };

        const adminProvider = new AdminProvider();
        try {
            const response = await adminProvider.createCoupon(couponPayload);
            setCouponMessage({ type: 'success', text: response.message || t('admin_coupon_created_success', { defaultValue: 'Coupon created successfully!' }) });
            setSelectedUser(''); // Clear selected user
        } catch (error) {
            console.error('Error creating coupon:', error);
            setCouponMessage({ type: 'error', text: error.message || t('admin_coupon_created_error', { defaultValue: 'Error creating coupon.' }) });
        } finally {
            setIsLoading(false);
        }
    };

    const handleLoadMoreSurveys = async () => {
        setIsLoading(true);
        const adminProvider = new AdminProvider();
        try {
            const nextSurveys = await adminProvider.getAllSurveys(i18n.language, 10, surveysOffset);
            setSurveys(prev => [...prev, ...nextSurveys]);
            setSurveysOffset(prevOffset => prevOffset + 10);
            if (nextSurveys.length < 10) {
                setHasMoreSurveys(false);
            }
        } catch (error) {
            console.error('Error loading more surveys:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleLoadMorePlays = async () => {
        setIsLoading(true);
        const adminProvider = new AdminProvider();
        try {
            const nextPlays = await adminProvider.getAllPlays(10, playsOffset);
            setPlaysHistory(prev => [...prev, ...nextPlays]);
            setPlaysOffset(prevOffset => prevOffset + 10);
            if (nextPlays.length < 10) {
                setHasMorePlays(false);
            }
        } catch (error) {
            console.error('Error loading more plays:', error);
        } finally {
            setIsLoading(false);
        }
    };

    // Keeping existing table functions but adjusting for admin token if they are to be used.
    const handleTableClick = async (tableName) => {
        try {
            // Ensure adminToken is passed for authenticated API calls
            const response = await axios.get(`/api/${tableName}`, {
                headers: {
                    'x-admin-token': adminToken, // Use the custom header for admin token
                },
            });
            setTableData(response.data);
            setSelectedTable(tableName);
        } catch (error) {
            console.error('Error fetching table data:', error);
            // Optionally show an error message
        }
    };

    const handleUpdate = async () => {
        try {
            // Example: Update data for the selected table
            // Ensure adminToken is passed for authenticated API calls
            await axios.put(`/api/${selectedTable}`, { data: updateData }, {
                headers: {
                    'x-admin-token': adminToken, // Use the custom header for admin token
                },
            });
            // Optionally, you can refresh the table data after updating
            handleTableClick(selectedTable);
        } catch (error) {
            console.error('Error updating table data:', error);
         // Optionally show an error message
        }
    };

    // Display an unauthorized message if no admin token is found
    // This check should happen early in the render cycle
    // Display an unauthorized message if no admin token is found
    if (!adminToken) {
        return (
            <div className="admin-container unauthorized">
                <h1 className="admin-title">{t('admin_panel', { defaultValue: 'Admin Panel' })}</h1>
                <p>{t('admin_unauthorized_access', { defaultValue: 'Unauthorized access. Please log in as an administrator.' })}</p>
                {/* You might want a button to navigate to the admin login page */}
            </div>
        );
    }

    return (
        <div className="admin-container">
            <h1 className="admin-title">{t('admin_panel', { defaultValue: 'Admin Panel' })}</h1>

            {/* Coupon Creation Section */}
            <div className="admin-section">
                <h2 className="section-title">{t('create_coupon', { defaultValue: 'Create Coupon' })}</h2>
                {isLoading && <p>{t('loading', { defaultValue: 'Loading' })}...</p>}
                {couponMessage.text && (
                    <p className={`message ${couponMessage.type}`}>{couponMessage.text}</p>
                )}
                <div className="form-group">
                    <label htmlFor="gameSelect">{t('select_game', { defaultValue: 'Select Game' })}:</label>
                    <select
                        id="gameSelect"
                        value={selectedGame}
                        onChange={(e) => setSelectedGame(parseInt(e.target.value))}
                        disabled={isLoading}
                    >
                        {games.length === 0 && <option value="">{t('no_games_available', { defaultValue: 'No games available' })}</option>}
                        {games.map(game => (
                            <option key={game.id} value={game.id}>{game.title} ({game.city})</option>
                        ))}
                    </select>
                </div>
                <div className="form-group">
                    <label htmlFor="userSelect">{t('assign_to_user', { defaultValue: 'Assign to User (Optional)' })}:</label>
                    <select
                        id="userSelect"
                        value={selectedUser || ''}
                        onChange={(e) => setSelectedUser(e.target.value ? parseInt(e.target.value) : null)}
                        disabled={isLoading}
                    >
                        <option value="">{t('none_select_user', { defaultValue: 'None (General Coupon)' })}</option>
                        {users.map(user => (
                            <option key={user.user_id} value={user.user_id}>{user.username} ({user.email})</option>
                        ))}
                    </select>
                </div>
                
              
               
               
                <button onClick={handleCreateCoupon} disabled={isLoading} className="submit-button">
                    {t('create_coupon_button', { defaultValue: 'Create Coupon' })}
                </button>
            </div>

            {/* Surveys List Section */}
            <div className="admin-section">
                <h2 className="section-title">{t('all_surveys', { defaultValue: 'All Submitted Surveys' })}</h2>
                {isLoading && <p>{t('loading', { defaultValue: 'Loading' })}...</p>}
                {surveys.length > 0 ? (
                    <div className="table-container">
                        <table>
                            <thead>
                                <tr>
                                    <th>{t('survey_id', { defaultValue: 'ID' })}</th>
                                    <th>{t('game_title', { defaultValue: 'Game' })}</th>
                                    <th>{t('submitted_by', { defaultValue: 'Submitted By' })}</th>
                                    <th>{t('experience_rating', { defaultValue: 'Experience' })}</th>
                                    <th>{t('difficulty_rating', { defaultValue: 'Difficulty' })}</th>
                                    <th>{t('had_problem', { defaultValue: 'Had Problem' })}</th>
                                    <th>{t('problem_details', { defaultValue: 'Problem Details' })}</th>
                                    <th>{t('map_recommendations', { defaultValue: 'Map Recs' })}</th>
                                    <th>{t('general_feedback', { defaultValue: 'General Feedback' })}</th>
                                    <th>{t('learned_about', { defaultValue: 'Learned About' })}</th>
                                    <th>{t('most_least_liked_riddle', { defaultValue: 'Most/Least Liked Riddle' })}</th>
                                    <th>{t('submitted_at', { defaultValue: 'Submitted At' })}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {surveys.map((survey) => (
                                    <tr key={survey.survey_id}>
                                        <td>{survey.survey_id}</td>
                                        <td>{survey.game_title}</td>
                                        <td>{survey.submitted_by_username || survey.submitted_by_email || 'Guest'}</td>
                                        <td>{survey.experienceRating}</td>
                                        <td>{t(survey.difficultyRating, { defaultValue: survey.difficultyRating })}</td> {/* Translate difficulty */}
                                        <td>{survey.hadProblem ? t('Yes', { defaultValue: 'Yes' }) : t('No', { defaultValue: 'No' })}</td>
                                        <td>{survey.problemDetails || '-'}</td>
                                        <td>{t(survey.mapRecommendations, { defaultValue: survey.mapRecommendations }) || '-'}</td> {/* Translate map recs */}
                                        <td>{survey.feedbackComment || '-'}</td>
                                        <td>{t(survey.learnedAbout, { defaultValue: survey.learnedAbout }) || survey.learnedAboutOther || '-'}</td> {/* Translate learned about */}
                                        <td>{survey.likedLeastMostRiddle || '-'}</td>
                                        <td>{new Date(survey.submitted_at).toLocaleString()}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {hasMoreSurveys && (
                            <button 
                                onClick={handleLoadMoreSurveys} 
                                disabled={isLoading} 
                                className="submit-button"
                                style={{ marginTop: '10px' }}
                            >
                                {t('load_more', { defaultValue: 'Load More' })}
                            </button>
                        )}
                    </div>
                ) : (
                    <p>{t('no_surveys_found', { defaultValue: 'No surveys have been submitted yet.' })}</p>
                )}
            </div>

            {/* Play IDs History Section */}
            <div className="admin-section">
                <h2 className="section-title">{t('admin_play_history', { defaultValue: 'Play History' })}</h2>
                {playsHistory.length > 0 ? (
                    <div className="table-container">
                        <table>
                            <thead>
                                <tr>
                                    <th>ID</th>
                                    <th>{t('game_title', { defaultValue: 'Game' })}</th>
                                    <th>{t('user_started', { defaultValue: 'User Started' })}</th>
                                    <th>{t('date_started', { defaultValue: 'Date Started' })}</th>
                                    <th>{t('game_status', { defaultValue: 'Status' })}</th>
                                    <th>{t('last_step', { defaultValue: 'Last Step' })}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {playsHistory.map((play) => (
                                    <tr 
                                        key={play.play_id} 
                                        onClick={() => navigate('/game-overview', { state: { game: play.game_id, play_id: play.play_id, isAdmin: true } })}
                                        style={{ cursor: 'pointer' }}
                                        className="clickable-row"
                                    >
                                        <td>{play.play_id}</td>
                                        <td>{play.game_title}</td>
                                        <td>{play.user_started || '-'}</td>
                                        <td>{new Date(play.start_date).toLocaleString()}</td>
                                        <td>{play.game_status ? t('completed', { defaultValue: 'Completed' }) : t('not_completed', { defaultValue: 'Not Completed' })}</td>
                                        <td>{play.last_step}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {hasMorePlays && (
                            <button 
                                onClick={handleLoadMorePlays} 
                                disabled={isLoading} 
                                className="submit-button"
                                style={{ marginTop: '10px' }}
                            >
                                {t('load_more', { defaultValue: 'Load More' })}
                            </button>
                        )}
                    </div>
                ) : (
                    <p>{t('no_data_for_table', { defaultValue: 'No data available for this table.' })}</p>
                )}
            </div>
            {/* Existing Table View (optional - uncomment if you want to keep this functionality) */}
            {/*
            <div className="admin-section">
                <h2 className="section-title">{t('manage_data', { defaultValue: 'Manage Database Tables' })}</h2>
                <div className="menu">
                    <ul>
                        <li onClick={() => handleTableClick('coupons')} className={selectedTable === 'coupons' ? 'active-menu-item' : ''}>
                            {t('coupons_table', { defaultValue: 'Coupons' })}
                        </li>
                        <li onClick={() => handleTableClick('play_details')} className={selectedTable === 'play_details' ? 'active-menu-item' : ''}>
                            {t('play_details_table', { defaultValue: 'Play Details' })}
                        </li>
                        <li onClick={() => handleTableClick('team_game')} className={selectedTable === 'team_game' ? 'active-menu-item' : ''}>
                            {t('team_game_table', { defaultValue: 'Team Games' })}
                        </li>
                        {/* Add more tables as needed *//*}
                    </ul>
                </div>
                <div className="table-container">
                    {selectedTable && (
                        <div>
                            <h3>{t('data_for', { defaultValue: 'Data for', tableName: selectedTable.toUpperCase() })}</h3>
                            {tableData.length > 0 ? (
                                <table>
                                    <thead>
                                        <tr>
                                            {Object.keys(tableData[0]).map((key) => (
                                                <th key={key}>{key}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {tableData.map((row, rowIndex) => (
                                            <tr key={rowIndex}>
                                                {Object.values(row).map((value, colIndex) => (
                                                    <td key={colIndex}>{String(value)}</td>
                                                ))}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            ) : (
                                <p>{t('no_data_for_table', { defaultValue: 'No data available for this table.' })}</p>
                            )}
                            {/* Example for update (you'd need a more robust editing solution) *//*}
                            <div className="update-section">
                                <textarea
                                    placeholder={t('update_json_data', { defaultValue: 'Enter JSON to update selected row (e.g., {"id": 1, "column": "new_value"})' })}
                                    value={updateData}
                                    onChange={(e) => setUpdateData(e.target.value)}
                                    rows="5"
                                    cols="50"
                                />
                                <button onClick={handleUpdate}>{t('update_record', { defaultValue: 'Update Record' })}</button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
            */}
        </div>
    );
};

export default AuthContainer;